import React, { useEffect, useState, useCallback } from "react";
import { Link } from 'react-router-dom';
import {
  IconButton,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Button,
  CircularProgress,
  Tooltip,
  Box,
} from "@mui/material";
import PageHeader1 from "../../components/common/PageHeader1";
import toast, { Toaster } from "react-hot-toast";
import { DataGrid, GridToolbar } from "@mui/x-data-grid";
import axios from "axios";
import { usePermissions } from "../../context/AuthContext";

function ExpertiseList() {
  const { can } = usePermissions();
  const token = localStorage.getItem("vedhvidhi-admin-token");

  const [rows, setRows] = useState([]);
  const [rowCount, setRowCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [open, setOpen] = useState(false);
  const [selectedExpertiseId, setSelectedExpertiseId] = useState(null);

  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: 10,
  });

  const [columnVisibilityModel, setColumnVisibilityModel] = useState({});
  const [filterModel, setFilterModel] = useState({
    items: [],
    quickFilterExcludeHiddenColumns: true,
    quickFilterValues: [""],
  });

  const fetchExpertiseData = useCallback(async () => {
    if (!token) {
      setError("No token found");
      setRows([]);
      setRowCount(0);
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const apiPage = paginationModel.page + 1;
      const limit = paginationModel.pageSize;

      const requestUrl = `${process.env.REACT_APP_BASEURL}/admin/expertise?page=${apiPage}&limit=${limit}`;

      const response = await axios.get(requestUrl, {
        headers: { Authorization: token },
      });

      const totalDocs = response?.data?.paginationDetail?.totalDocs || 0;
      const expertiseList = response?.data?.data || [];

      const formattedRows = expertiseList.map((item, index) => ({
        id: item._id,
        sono: paginationModel.page * paginationModel.pageSize + index + 1,
        name: item.expertiseName || "N/A",
        expertiseIcon: item?.expertiseIcon || "N/A",
        createdAt: item?.createdAt
          ? new Date(item.createdAt).toLocaleString()
          : "",
      }));

      setRows(formattedRows);
      setRowCount(totalDocs);

      const totalPages = Math.ceil(totalDocs / paginationModel.pageSize);
      if (paginationModel.page >= totalPages && totalPages > 0) {
        setPaginationModel((prev) => ({
          ...prev,
          page: totalPages - 1,
        }));
      }
    } catch (err) {
      console.error("Error fetching expertise:", err);
      setError("Failed to fetch data");
      setRows([]);
      setRowCount(0);
    } finally {
      setLoading(false);
    }
  }, [paginationModel.page, paginationModel.pageSize, token]);

  useEffect(() => {
    fetchExpertiseData();
  }, [fetchExpertiseData]);

  const handleClickOpen = (id) => {
    setSelectedExpertiseId(id);
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    setSelectedExpertiseId(null);
  };

  const handleDelete = async () => {
    if (!selectedExpertiseId) return;

    setLoading(true);
    try {
      await axios.delete(
        `${process.env.REACT_APP_BASEURL}/admin/expertise/${selectedExpertiseId}`,
        { headers: { Authorization: token } }
      );
      toast.success("Expertise deleted successfully!");
      handleClose();
      await fetchExpertiseData();
    } catch (error) {
      toast.error("Error deleting expertise");
      setLoading(false);
    }
  };

  const canEdit = can("expertise:edit");
  const canDelete = can("expertise:delete");

  const actionField =
    canEdit || canDelete
      ? [
        {
          field: "actions",
          headerName: "Actions",
          width: 100,
          sortable: false,
          filterable: false,
          renderCell: ({ row }) => (
            <Box display="flex" alignItems="center" gap={0.5}>
              {canEdit && (
                <Tooltip title="Edit Expertise">
                  <IconButton component={Link} to={`/expertise-add?id=${row?._id || row?.id}`} color="primary">
                    <i className="icofont-edit text-info"></i>
                  </IconButton>
                </Tooltip>
              )}
              {canDelete && (
                <Tooltip title="Delete Expertise">
                  <IconButton color="error" onClick={() => handleClickOpen(row._id || row.id)}>
                    <i className="icofont-ui-delete text-danger"></i>
                  </IconButton>
                </Tooltip>
              )}
            </Box>
          ),
        },
      ]
      : [];

  const columns = [
    { field: "sono", headerName: "S.No", width: 100, sortable: false },
    {
      field: "expertiseIcon",
      headerName: "Icon",
      width: 150,
      sortable: false,
      renderCell: (params) =>
        params?.value && params.value !== "N/A" ? (
          <img
            src={params.value}
            alt="Icon"
            style={{ width: 40, height: 40, objectFit: "contain" }}
          />
        ) : (
          <span>No Icon</span>
        ),
    },
    { field: "name", headerName: "Expertise Name", width: 250 },
    { field: "createdAt", headerName: "Created At", width: 250 },
    ...actionField,
  ];


  return (
    <>
      <Toaster position="top-center" />
      <div className="body d-flex py-1">
        <div className="container-xxl">
          <PageHeader1
            pagetitle="Expertise List"
            righttitle="Add Expertise"
            link="/expertise-add"
            routebutton={can('expertise:add')}
          />
          <div className="row g-0 mb-3">
            <div className="col-md-12">
              <div className="card">
                <div
                  className="card-body"
                  style={{ position: "relative", minHeight: "400px" }}
                >
                  {error ? (
                    <div className="alert alert-danger mt-3">
                      <strong>Error:</strong> {error}
                    </div>
                  ) : (
                    <>
                      <DataGrid
                        rows={rows}
                        columns={columns}
                        loading={loading}
                        paginationMode="server"
                        rowCount={rowCount}
                        paginationModel={paginationModel}
                        onPaginationModelChange={setPaginationModel}
                        pageSizeOptions={[10, 25, 50, 100]}
                        getRowId={(row) => row.id}
                        disableRowSelectionOnClick
                        slots={{ toolbar: GridToolbar }}
                        filterModel={filterModel}
                        onFilterModelChange={(newModel) => setFilterModel(newModel)}
                        slotProps={{ toolbar: { showQuickFilter: true } }}
                        columnVisibilityModel={columnVisibilityModel}
                        onColumnVisibilityModelChange={(newModel) =>
                          setColumnVisibilityModel(newModel)
                        }
                        autoHeight
                      />
                      {loading && (
                        <div
                          style={{
                            position: "absolute",
                            top: "50%",
                            left: "50%",
                            transform: "translate(-50%, -50%)",
                            zIndex: 10,
                          }}
                        >
                          <CircularProgress />
                        </div>
                      )}
                    </>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Delete Confirmation Dialog */}
      <Dialog open={open} onClose={handleClose}>
        <DialogTitle>Delete Confirmation</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete this expertise?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose}>Cancel</Button>
          <Button onClick={handleDelete} color="error" disabled={loading}>
            {loading ? <CircularProgress size={20} /> : "Delete"}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}

export default ExpertiseList;